import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Table, Spinner, Badge, Row, Card, Button } from 'react-bootstrap';
import axios from 'axios';
import { toWords } from 'number-to-words';
import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';
import Swal from 'sweetalert2';

const ViewSalesReturnPD = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [salesReturnData, setSalesReturnData] = useState(null);
  const [rows, setRows] = useState([]);

  const fetchSalesReturnData = async () => {
    try {
      const response = await axios.get(`${BASE_URL}salesReturn/findById/${id}`, {
        headers: authHeader()
      });

      const data = response.data;
      setSalesReturnData(data);

      if (data.salesReturnItems && data.salesReturnItems.length > 0) {
        setRows(data.salesReturnItems.map(item => ({
          description: item.product?.description || '-',
          productremarks: item.productremarks || "-",
          quantity: item.quantity || 0,
          unitrate: item.unitrate || 0,
          amount: item.amount || 0,
          hsnsacno: item.hsnsacno || "-",
          partno: item.partno || "-",
          serialnumber: item.serialnumber || "-",
          modelnumber: item.modelnumber || "-",
          tagnumber: item.tagnumber || "-",
          moc: item.moc || "-",
          itemcode: item.itemcode || "-",
          uom: item.uom || "-"
        })));
      }
    } catch (error) {
      console.error("Error fetching sales return data", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch sales return data. Please try again.",
      });
      navigate("/salesReturnIndexTable/1");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesReturnData();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatNumberWithCommas = (num) => {
    if (isNaN(num) || num === null || num === undefined) return '0.00';
    return parseFloat(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const numberToWordsWithRupees = (num) => {
    const amount = Math.round(num);
    const words = toWords(amount);

    // Capitalize first letter of each word
    const capitalizedWords = words.split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');

    return `${capitalizedWords} Rupees Only`;
  };

  if (isLoading) {
    return (
      <div className="container text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading sales return details...</p>
      </div>
    );
  }

  if (!salesReturnData) {
    return (
      <div className="container text-center py-5">
        <div className="alert alert-warning">No sales return data found</div>
      </div>
    );
  }

  // Calculate totals
  const nettotal = rows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const pAndF = nettotal * (salesReturnData.packingandforwardingpercent / 100);
  const freight = nettotal * (salesReturnData.freightpercent / 100);
  const subtotal = nettotal + pAndF + freight;
  const gst = subtotal * (salesReturnData.gstpercent / 100);
  const grandtotalinr = subtotal + gst;

  return (
    <div className="container py-4">
      {/* Header Information Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Return Number:</strong>
                <span>{salesReturnData.salesreturnno || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Return Date:</strong>
                <span>{formatDate(salesReturnData.salesreturndate)}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Invoice Number:</strong>
                <span>{salesReturnData.invoiceno || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Invoice Date:</strong>
                <span>{formatDate(salesReturnData.invoicedate)}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>DC Number:</strong>
                <span>{salesReturnData.dcno || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>DC Date:</strong>
                <span>{formatDate(salesReturnData.dcdate)}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Buyer Order No:</strong>
                <span>{salesReturnData.buyerorderno || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Buyer Order Date:</strong>
                <span>{formatDate(salesReturnData.buyerorderdate)}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Dispatch Through:</strong>
                <span>{salesReturnData.dispatchthrough || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Destination:</strong>
                <span>{salesReturnData.destination || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Delivery Term:</strong>
                <span>{salesReturnData.termsofdelivery || '-'}</span>
              </div>
            </Col>
            {salesReturnData.reference && (
              <Col md={12} className="mb-3">
                <div>
                  <strong className="me-2">Other Reference:</strong>
                  <div className="ms-4 mt-1">{salesReturnData.reference}</div>
                </div>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Customer Information Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Header>
          <h5 className="mb-0">Customer Details</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Customer:</strong>
                <span>{salesReturnData.customer?.companyname || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Contact Person:</strong>
                <span>{salesReturnData.contactperson || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Contact No:</strong>
                <span>{salesReturnData.contactno || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Email:</strong>
                <span>{salesReturnData.email || '-'}</span>
              </div>
            </Col>

            <Col md={12}>
              <p>
                <strong>Customer Address:</strong>{" "}
                {salesReturnData.customeraddress?.customeraddress || "-"}
              </p>

              <p>
                {salesReturnData.customeraddress?.city?.cityname || "-"},{" "}
                {salesReturnData.customeraddress?.state?.statename || "-"},{" "}
                {salesReturnData.customeraddress?.pincode || "-"}
              </p>

              <p>
                {salesReturnData.customeraddress?.country?.countryname || "-"}
              </p>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Bill To:</strong>
                <span>
                  {salesReturnData.billto?.customeraddress || '-'}
                  <div className="ms-4 mt-1">
                    {salesReturnData.billto?.city?.cityname || '-'}, {salesReturnData.billto?.state?.statename || '-'}, {salesReturnData.billto?.pincode || '-'}
                    <br />
                    {salesReturnData.billto?.country?.countryname || '-'}
                  </div>
                </span>
              </div>
            </Col>

            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Ship To:</strong>
                <span>
                  {salesReturnData.shipto?.customeraddress || '-'}
                  <div className="ms-4 mt-1">
                    {salesReturnData.shipto?.city?.cityname || '-'}, {salesReturnData.shipto?.state?.statename || '-'}, {salesReturnData.shipto?.pincode || '-'}
                    <br />
                    {salesReturnData.shipto?.country?.countryname || '-'}
                  </div>
                </span>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Branch & Warehouse Details */}
      <Card className="mb-4 shadow-sm">
        <Card.Header>
          <h5 className="mb-0">Branch & Warehouse Details</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Branch:</strong>
                <span>{salesReturnData.branch?.branchname || '-'}</span>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex">
                <strong className="me-2" style={{ minWidth: '180px' }}>Warehouse:</strong>
                <span>{salesReturnData.warehouse?.warehousename || '-'}</span>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Sales Return Items Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Header>
          <h5 className="mb-0">Sales Return Items</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table striped bordered hover className="mb-0">
              <thead>
                <tr>
                  <th width="40">#</th>
                  <th>Product Description</th>
                  <th>Part No</th>
                  <th>Serial No</th>
                  <th>Model No</th>
                  <th>Tag No</th>
                  <th>MOC</th>
                  <th>HSN Code</th>
                  <th>Item Code</th>
                  <th>Quantity</th>
                  <th>UOM</th>
                  <th>Unit Rate (₹)</th>
                  <th>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      {row.description}
                      {row.productremarks && row.productremarks !== '-' && (
                        <div className="text-muted small mt-2">
                          <strong>Remarks:</strong> {row.productremarks}
                        </div>
                      )}
                    </td>
                    <td>{row.partno}</td>
                    <td>{row.serialnumber}</td>
                    <td>{row.modelnumber}</td>
                    <td>{row.tagnumber}</td>
                    <td>{row.moc}</td>
                    <td>{row.hsnsacno}</td>
                    <td>{row.itemcode}</td>
                    <td className="text-end">{row.quantity || '0'}</td>
                    <td>{row.uom}</td>
                    <td className="text-end">{formatNumberWithCommas(row.unitrate)}</td>
                    <td className="text-end">{formatNumberWithCommas(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Calculation Summary Card */}
      {/* <Card className="mb-4 shadow-sm">
        <Card.Header>
          <h5 className="mb-0">Amount Calculation</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table striped bordered hover className="mb-0">
              <thead>
                <tr>
                  <th width="50%">Description</th>
                  <th width="20%">Percentage</th>
                  <th width="30%">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Net Total</td>
                  <td>-</td>
                  <td className="text-start">{formatNumberWithCommas(nettotal)}</td>
                </tr>
                <tr>
                  <td>Packing & Forwarding</td>
                  <td className="text-start">{salesReturnData.packingandforwardingpercent || '0'}%</td>
                  <td className="text-start">{formatNumberWithCommas(pAndF)}</td>
                </tr>
                <tr>
                  <td>Freight</td>
                  <td className="text-start">{salesReturnData.freightpercent || '0'}%</td>
                  <td className="text-start">{formatNumberWithCommas(freight)}</td>
                </tr>
                <tr>
                  <td>Subtotal</td>
                  <td>-</td>
                  <td className="text-start">{formatNumberWithCommas(subtotal)}</td>
                </tr>
                <tr>
                  <td>GST</td>
                  <td className="text-start">{salesReturnData.gstpercent || '18'}%</td>
                  <td className="text-start">{formatNumberWithCommas(gst)}</td>
                </tr>
                <tr className="table-success">
                  <td><strong>Grand Total</strong></td>
                  <td>-</td>
                  <td className="text-start"><strong>{formatNumberWithCommas(grandtotalinr)}</strong></td>
                </tr>
                <tr>
                  <td colSpan="3">
                    <div className="p-2">
                      <strong>Grand Total in Words:</strong>
                      <div className="ms-2 mt-1 fst-italic">{numberToWordsWithRupees(grandtotalinr)}</div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card> */}

      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">Amount Calculation</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="list-group list-group-flush">
            {/* Net Total */}
            <div className="list-group-item">
              <div className="row g-0 align-items-center">
                <div className="col-6 ps-3">Net Total</div>
                <div className="col-2">-</div>
                <div className="col-4 text-end pe-3">{formatNumberWithCommas(nettotal)}</div>
              </div>
            </div>

            {/* Packing & Forwarding */}
            <div className="list-group-item">
              <div className="row g-0 align-items-center">
                <div className="col-6 ps-3">Packing & Forwarding</div>
                <div className="col-2">{salesReturnData.packingandforwardingpercent || '0'}%</div>
                <div className="col-4 text-end pe-3">{formatNumberWithCommas(pAndF)}</div>
              </div>
            </div>

            {/* Freight */}
            <div className="list-group-item">
              <div className="row g-0 align-items-center">
                <div className="col-6 ps-3">Freight</div>
                <div className="col-2">{salesReturnData.freightpercent || '0'}%</div>
                <div className="col-4 text-end pe-3">{formatNumberWithCommas(freight)}</div>
              </div>
            </div>

            {/* Subtotal */}
            <div className="list-group-item">
              <div className="row g-0 align-items-center">
                <div className="col-6 ps-3">Subtotal</div>
                <div className="col-2">-</div>
                <div className="col-4 text-end pe-3">{formatNumberWithCommas(subtotal)}</div>
              </div>
            </div>

            {/* GST */}
            <div className="list-group-item">
              <div className="row g-0 align-items-center">
                <div className="col-6 ps-3">GST</div>
                <div className="col-2">{salesReturnData.gstpercent || '18'}%</div>
                <div className="col-4 text-end pe-3">{formatNumberWithCommas(gst)}</div>
              </div>
            </div>

            {/* Grand Total */}
            <div className="list-group-item bg-success bg-opacity-10">
              <div className="row g-0 align-items-center">
                <div className="col-6 ps-3"><strong>Grand Total</strong></div>
                <div className="col-2">-</div>
                <div className="col-4 text-end pe-3"><strong>{formatNumberWithCommas(grandtotalinr)}</strong></div>
              </div>
            </div>

            {/* Grand Total in Words */}
            <div className="list-group-item">
              <div className="p-3">
                <div className="d-flex flex-column">
                  <strong>Grand Total in Words:</strong>
                  <div className="mt-1 fst-italic">{numberToWordsWithRupees(grandtotalinr)}</div>
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end mb-4">
        <Button
          variant="outline-secondary"
          onClick={() => navigate(-1)}
          className="me-2"
        >
          <i className="bi bi-arrow-left me-2"></i>BACK
        </Button>
        {/* <Button
          variant="primary"
          onClick={() => navigate(`/salesReturnEdit/${id}`)}
        >
          <i className="bi bi-pencil me-2"></i>EDIT
        </Button> */}
      </div>
    </div>
  );
};

export default ViewSalesReturnPD;

